import React from 'react';
import { Radio } from 'lucide-react';
import { useAudioFocusSlot } from '../lib/audioFocus';

/**
 * FederationRadio — Live365 stream (homepage body slot).
 * Participates in the AudioFocus coordinator: renders the real iframe
 * only when it holds focus; otherwise shows a "signal parked" tile.
 */
export default function FederationRadio() {
  const { isActive, wrapperRef } = useAudioFocusSlot('home');

  return (
    <section
      data-testid="federation-radio"
      aria-label="Federation Radio — Live365 stream"
      className="relative bg-gradient-to-b from-black via-[#0d1a15] to-black border-y border-emerald-500/20"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, #10b981 1px, transparent 0)',
          backgroundSize: '22px 22px',
        }}
      />
      <div className="relative container mx-auto px-4 py-12">
        <div className="flex flex-col items-center text-center">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="relative inline-flex w-2 h-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <span className="text-[10px] font-mono uppercase tracking-[0.32em] text-emerald-300/90">
              Federation Radio · Live
            </span>
          </div>
          <h2 className="inline-flex items-center gap-2 text-2xl md:text-3xl font-extrabold text-white mb-2">
            <Radio className="w-5 h-5 text-emerald-300" />
            📻 Federation Radio — Live
          </h2>
          <p className="text-xs md:text-sm text-emerald-200/75 italic mb-8">
            The voice of the NextXus Federation · 24/7 signal
          </p>

          <div
            ref={wrapperRef}
            className="w-full max-w-[450px] rounded-2xl overflow-hidden border-2 border-emerald-500/40 shadow-2xl shadow-emerald-500/25"
            data-testid="federation-radio-frame-wrapper"
            style={{
              boxShadow:
                '0 0 30px rgba(16,185,129,0.22), 0 0 70px rgba(16,185,129,0.12)',
            }}
          >
            <div
              className="relative w-full"
              style={{ paddingTop: `${(316 / 450) * 100}%` }}
            >
              {isActive ? (
                <iframe
                  title="Federation Radio"
                  src="https://live365.com/embeds/v1/player/a16924?s=md&m=dark&c=mp3"
                  frameBorder="0"
                  data-testid="federation-radio-iframe"
                  className="absolute inset-0 w-full h-full"
                  style={{ backgroundColor: '#0b0a14' }}
                  allow="autoplay; encrypted-media"
                  loading="lazy"
                />
              ) : (
                <div
                  data-testid="federation-radio-parked"
                  className="absolute inset-0 flex flex-col items-center justify-center bg-[#0b0a14] text-emerald-300/70 gap-2"
                >
                  <Radio className="w-10 h-10 opacity-40" />
                  <p className="text-xs font-mono uppercase tracking-[0.22em]">
                    Signal parked
                  </p>
                  <p className="text-[10px] text-white/40">
                    Playing below · scroll to resume here
                  </p>
                </div>
              )}
            </div>
          </div>

          <p className="mt-4 text-[10px] font-mono uppercase tracking-[0.22em] text-white/40">
            Live365 · Station a16924 ·{' '}
            <a
              href="/radio"
              data-testid="federation-radio-fullpage-link"
              className="text-emerald-300 hover:text-emerald-200 transition-colors"
            >
              Open full page →
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
